import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import axios from "axios";
import Navbar from "./components/Navbar";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import Whitelist from "./pages/Whitelist";
import Blacklist from "./pages/Blacklist";
import CallHistory from "./pages/CallHistory";
import { SoftphoneProvider } from "./context/SoftphoneContext";
import MakeCall from "./pages/MakeCall";
import Contacts from "./pages/Contacts";
import Messages from "./pages/Messages";
import ChatConversation from "./pages/ChatConversation";
import ForgotPassword from "./pages/ForgotPassword";

// Token storage helpers for dual-lifetime (localStorage vs sessionStorage)
const getStoredToken = () => {
  return localStorage.getItem("token") || sessionStorage.getItem("token");
};

const removeStoredToken = () => {
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");
};

// Configure axios defaults
// axios.defaults.baseURL = "http://localhost:5000";
axios.defaults.baseURL = "https://app.callbunkerapp.com";
axios.defaults.withCredentials = true;

// Add axios interceptor to handle session invalidation dynamically
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.warn("Session expired or unauthorized. Notifying Flutter wrapper.");
      removeStoredToken();
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler("onSessionInvalidated");
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastBadgeCountRef = useRef(-1);

  const fetchUnreadCount = async () => {
    const token = getStoredToken();
    if (!token) return;
    try {
      const res = await axios.get("/api/sms/unread-count", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const count = res.data.unreadCount || 0;
        setUnreadCount(count);
        if (window.flutter_inappwebview && lastBadgeCountRef.current !== count) {
          lastBadgeCountRef.current = count;
          window.flutter_inappwebview.callHandler("updateAppBadge", count);
        }
      }
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    // Expose deep linking handler for native Flutter wrapper
    window.navigateToConversation = (phoneNumber) => {
      if (phoneNumber) {
        window.location.href = `/messages/${encodeURIComponent(phoneNumber)}`;
      }
    };

    // Intercept JWT token passed from Flutter via URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const queryToken = urlParams.get("auth");
    if (queryToken) {
      localStorage.setItem("token", queryToken);
      // Strip token from browser address bar for security
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const token = getStoredToken();
    if (token) {
      checkAuth();
      fetchUnreadCount();
    } else {
      setLoading(false);
    }

    // Poll unread count periodically
    const unreadTimer = setInterval(() => {
      fetchUnreadCount();
    }, 6000);

    return () => clearInterval(unreadTimer);
  }, []);

  const checkAuth = async () => {
    const token = getStoredToken();
    if (token) {
      try {
        const response = await axios.get("/api/user/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetchedUser = response.data.user;
        setUser(fetchedUser);

        // Sync auth and subscription state to native container
        if (window.flutter_inappwebview) {
          const isRemembered =
            localStorage.getItem("callbunker_remember_me") === "true";
          window.flutter_inappwebview.callHandler("onAuthStatusCheck", {
            userId: fetchedUser._id || fetchedUser.id,
            token: token,
            isSubscribed: fetchedUser.isSubscribed || false,
            rememberMe: isRemembered,
          });
        }
      } catch (error) {
        console.error("Failed to check auth status:", error);
        removeStoredToken();
        if (window.flutter_inappwebview) {
          window.flutter_inappwebview.callHandler("onSessionInvalidated");
        }
      }
    }
    setLoading(false);
  };

  const login = async (userData, token, rememberMe = true) => {
    if (rememberMe) {
      localStorage.setItem("token", token);
      sessionStorage.removeItem("token");
    } else {
      sessionStorage.setItem("token", token);
      localStorage.removeItem("token");
    }
    setUser(userData); // temporary fallback

    try {
      const response = await axios.get("/api/user/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const loggedUser = response.data.user;
      setUser(loggedUser);

      // Sync auth and subscription state to native container after successful login
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler("onAuthStatusCheck", {
          userId: loggedUser._id || loggedUser.id,
          token: token,
          isSubscribed: loggedUser.isSubscribed || false,
          rememberMe: Boolean(rememberMe),
        });
      }
    } catch (error) {
      console.error("Failed to fetch full profile after login:", error);
      removeStoredToken();
      setUser(null);
    }
  };

  const logout = async () => {
    try {
      await axios.post("/api/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    removeStoredToken();

    // Sync logout to native container to clear keys
    if (window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler("onSessionInvalidated");
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        {user && (
          <Navbar
            user={user}
            onLogout={logout}
            unreadCount={unreadCount}
          />
        )}
        {user ? (
          <SoftphoneProvider user={user}>
            <Routes>
              <Route
                path="/login"
                element={
                  user ? (
                    <Navigate to="/dashboard" />
                  ) : (
                    <Login onLogin={login} />
                  )
                }
              />
              <Route
                path="/signup"
                element={
                  user ? (
                    <Navigate to="/dashboard" />
                  ) : (
                    <Signup onLogin={login} />
                  )
                }
              />
              <Route
                path="/dashboard"
                element={
                  user ? <Dashboard user={user} /> : <Navigate to="/login" />
                }
              />
              <Route
                path="/settings"
                element={
                  user ? (
                    <Settings user={user} setUser={setUser} />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/messages"
                element={
                  user ? <Messages user={user} /> : <Navigate to="/login" />
                }
              />
              <Route
                path="/messages/:phoneNumber"
                element={
                  user ? (
                    <ChatConversation user={user} />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/whitelist"
                element={
                  user ? <Whitelist user={user} /> : <Navigate to="/login" />
                }
              />
              <Route
                path="/blacklist"
                element={
                  user ? <Blacklist user={user} /> : <Navigate to="/login" />
                }
              />
              <Route
                path="/contacts"
                element={user ? <Contacts /> : <Navigate to="/login" />}
              />
              <Route
                path="/calls"
                element={
                  user ? <CallHistory user={user} /> : <Navigate to="/login" />
                }
              />
              <Route
                path="/"
                element={<Navigate to={user ? "/dashboard" : "/login"} />}
              />
              <Route path="/make-call" element={<MakeCall user={user} />} />
              <Route path="/forgot-password" element={<Navigate to="/dashboard" />} />
            </Routes>
          </SoftphoneProvider>
        ) : (
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login onLogin={login} />} />
            <Route path="/signup" element={<Signup onLogin={login} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        )}
      </div>
    </Router>
  );
}

export default App;
